/** 连接、麦克风、播放和转写之间的最小语音控制器。 */

import { MicrophoneAudioSender, MicrophoneCapture } from "./microphone";
import { answerClientAction } from "./page-context-action";
import type { VoiceMode } from "../config";
import type { Conversation } from "../conversation/conversation";
import {
  createVoiceEventId,
  SHOPPER_SESSION_ENDED_CLOSE,
  VOICE_TERMINAL_CLOSE_CODES,
  VoiceGatewayConnection,
  type VoiceGatewayEvent,
} from "./protocol";
import { VoicePlayback } from "./playback";
import * as m from "../paraglide/messages.js";

export type VoiceStatus =
  "off" | "connecting" | "listening" | "answering" | "error";
export interface VoiceSnapshot {
  enabled: boolean;
  status: VoiceStatus;
  error?: string;
}

export interface VoiceController {
  getSnapshot: () => VoiceSnapshot;
  subscribe: (listener: () => void) => () => void;
  toggle: () => void;
  stop: () => void;
  dispose: () => void;
}

function stringField(
  event: VoiceGatewayEvent,
  key: string,
): string | undefined {
  const value = event[key];
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

function positiveIntegerField(
  event: VoiceGatewayEvent,
  key: string,
): number | undefined {
  const value = event[key];
  return typeof value === "number" && Number.isInteger(value) && value > 0
    ? value
    : undefined;
}

function errorText(error: unknown): string {
  if (error instanceof Error && error.message !== "") return error.message;
  return m.widget_voice_error();
}

export function createVoiceController(
  url: string,
  voiceMode: VoiceMode | undefined,
  conversation: Conversation,
): VoiceController {
  let snapshot: VoiceSnapshot = {
    enabled: false,
    status: "off",
  };
  let disposed = false;
  let targetRate = 0;
  let voiceReady = false;
  let context: AudioContext | undefined;
  let playback: VoicePlayback | undefined;
  let socket: VoiceGatewayConnection | undefined;
  const capture = new MicrophoneCapture();
  const sender = new MicrophoneAudioSender();
  const listeners = new Set<() => void>();
  let startGeneration = 0;

  const notify = () => {
    if (disposed) return;
    for (const listener of listeners) listener();
  };
  const setSnapshot = (next: VoiceSnapshot) => {
    snapshot = next;
    notify();
  };
  const setStatus = (status: VoiceStatus, error?: string) => {
    setSnapshot({
      ...snapshot,
      status,
      error,
    });
  };
  const sendReceipt = (
    type: "playback.started" | "playback.ended" | "playback.cancelled",
    responseId: string,
    reason?: string,
  ) => {
    socket?.send({
      type,
      event_id: createVoiceEventId(),
      responseId,
      ...(reason === undefined ? {} : { reason }),
    });
  };
  const stopResources = () => {
    sender.reset();
    targetRate = 0;
    voiceReady = false;
    capture.stop();
    playback?.dispose();
    playback = undefined;
    context?.close().catch(() => undefined);
    context = undefined;
    socket?.stop();
    socket = undefined;
  };
  const fail = (message: string) => {
    stopResources();
    setSnapshot({
      ...snapshot,
      enabled: false,
      status: "error",
      error: message,
    });
  };
  const handleEvent = (event: VoiceGatewayEvent) => {
    const type = event.type;
    if (type === "client.action.request") {
      const requestSocket = socket;
      const generation = startGeneration;
      answerClientAction(event, {
        readContext: () => conversation.readMessageContext(),
        send: (result) => requestSocket?.send(result) ?? false,
        isCurrent: () =>
          socket === requestSocket && generation === startGeneration,
      });
      return;
    }
    if (type === "session.ready") return;
    if (type === "voice.ready") {
      const rate = positiveIntegerField(event, "inputSampleRate");
      if (rate === undefined) {
        fail(m.widget_voice_error());
        return;
      }
      targetRate = rate;
      voiceReady = true;
      sender.reset();
      setStatus("listening");
      return;
    }
    if (type === "voice.connection") {
      const state = stringField(event, "state");
      if (state === "connected" && snapshot.status === "error")
        setStatus(voiceReady ? "listening" : "connecting");
      if (state !== "connected") {
        voiceReady = false;
        targetRate = 0;
        sender.reset();
        if (snapshot.enabled) setStatus("connecting");
      }
      if (state === "unavailable" || state === "disconnected")
        fail(m.widget_voice_error());
      return;
    }
    if (type === "shopper.question") {
      const conversationId = stringField(event, "conversation_id");
      const messageId = stringField(event, "message_id");
      const content = stringField(event, "content");
      if (!conversationId || !messageId || !content || !voiceCredentialUid)
        return;
      if (
        !conversation.addVoiceQuestion(
          { conversationId, messageId, content },
          voiceCredentialUid,
        )
      ) {
        fail(m.widget_voice_ended());
      }
      return;
    }
    if (type === "voice.state") {
      if (!voiceReady) return;
      const state = stringField(event, "state");
      if (state === "processing" || state === "speaking")
        setStatus("answering");
      else if (state === "listening" || state === "idle")
        setStatus("listening");
      return;
    }
    if (type === "audio.delta") {
      const audio = stringField(event, "audio");
      const responseId = stringField(event, "responseId");
      const rate = positiveIntegerField(event, "sampleRate");
      if (audio && responseId && rate !== undefined)
        playback?.append(audio, rate, responseId);
      return;
    }
    if (type === "audio.done") {
      const responseId = stringField(event, "responseId");
      if (responseId) playback?.done(responseId);
      return;
    }
    if (type === "playback.clear" || type === "response.interrupted") {
      playback?.clear(stringField(event, "reason") ?? "interrupted");
      return;
    }
    if (type === "error") {
      // Gateway 的 error 只代表这一轮失败，通话仍可继续。
      setStatus("error", m.widget_voice_turn_failed());
    }
  };
  let voiceCredentialUid: string | undefined;
  const start = () => {
    if (disposed || snapshot.enabled) return;
    const generation = ++startGeneration;
    voiceReady = false;
    setSnapshot({
      ...snapshot,
      enabled: true,
      status: "connecting",
      error: undefined,
    });
    let startContext: AudioContext;
    void (async () => {
      try {
        startContext = new AudioContext();
        context = startContext;
        void startContext.resume();
        playback = new VoicePlayback(startContext, sendReceipt);
        const credential = await conversation.getVoiceCredential();
        if (!snapshot.enabled || generation !== startGeneration) return;
        voiceCredentialUid = credential.uid;
        socket = new VoiceGatewayConnection(
          url,
          credential.token,
          credential.conversationId,
          voiceMode,
          {
            onEvent: handleEvent,
            onError: (error) => {
              fail(errorText(error));
            },
            onClose: (code) => {
              if (disposed || !snapshot.enabled) return;
              fail(
                code === SHOPPER_SESSION_ENDED_CLOSE
                  ? m.widget_voice_ended()
                  : VOICE_TERMINAL_CLOSE_CODES.has(code)
                    ? code === 4002
                      ? m.widget_voice_occupied()
                      : code === 4001
                        ? m.widget_voice_replaced()
                        : m.widget_voice_revoked()
                    : m.widget_voice_closed(),
              );
            },
          },
        );
        socket.connect();
        void capture
          .start(startContext, {
            onSamples: (samples, sampleRate) => {
              if (!snapshot.enabled || generation !== startGeneration) return;
              sender.send(samples, sampleRate, targetRate, socket?.webSocket);
            },
          })
          .catch((error: unknown) => {
            if (snapshot.enabled && generation === startGeneration)
              fail(errorText(error));
          });
      } catch (error) {
        if (snapshot.enabled && generation === startGeneration)
          fail(errorText(error));
      }
    })();
  };
  const stop = () => {
    if (!snapshot.enabled && snapshot.status === "off") return;
    startGeneration += 1;
    voiceCredentialUid = undefined;
    stopResources();
    setSnapshot({
      ...snapshot,
      enabled: false,
      status: "off",
      error: undefined,
    });
  };

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    toggle: () => {
      if (snapshot.enabled) stop();
      else start();
    },
    stop,
    dispose: () => {
      disposed = true;
      startGeneration += 1;
      voiceCredentialUid = undefined;
      stopResources();
      listeners.clear();
    },
  };
}
